/**
 * @fileoverview 陸上管理者 → 船員端末へのネットワーク音声通話（VoIP）発信モーダル
 * @description デバイスを指定して発信する。シグナリングWS(/api/voice/signal)へ role=admin で接続し、
 *   invite→(端末が応答)→WebRTCのoffer/answer/ICE交換で音声通話を確立する。
 *   メディアはP2P/TURN経由（サーバーは通らない）。マイクはPCのものを使用する。
 */
import React, { useEffect, useRef, useState } from "react";
import { getApiBase, getToken } from "../services/api";

interface IceServer { urls: string | string[]; username?: string; credential?: string; }

type Phase = "idle" | "calling" | "ringing" | "connecting" | "in_call" | "ended";

function adminCompanyId(): string {
  try {
    const u = JSON.parse(localStorage.getItem("admin_user") ?? "{}");
    return String(u.company_id ?? "");
  } catch { return ""; }
}

function adminUserName(): string {
  try {
    const u = JSON.parse(localStorage.getItem("admin_user") ?? "{}");
    return String(u.user_name ?? "陸上管理者");
  } catch { return "陸上管理者"; }
}

export const CallModal: React.FC<{
  deviceId: string;
  deviceName: string;
  onClose: () => void;
}> = ({ deviceId, deviceName, onClose }) => {
  const [phase, setPhase] = useState<Phase>("idle");
  const [error, setError] = useState<string | null>(null);
  const [muted, setMuted] = useState(false);
  const [peerOnline, setPeerOnline] = useState<boolean | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const iceServersRef = useRef<IceServer[]>([{ urls: "stun:stun.l.google.com:19302" }]);
  const pendingRemoteIceRef = useRef<RTCIceCandidateInit[]>([]);
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const phaseRef = useRef<Phase>("idle");
  phaseRef.current = phase;

  const sendSig = (obj: unknown) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(obj));
  };

  const cleanup = (notifyPeer: boolean) => {
    if (notifyPeer) sendSig({ type: "bye" });
    try { pcRef.current?.close(); } catch { /* ignore */ }
    pcRef.current = null;
    localStreamRef.current?.getTracks().forEach(t => t.stop());
    localStreamRef.current = null;
    if (audioElRef.current) audioElRef.current.srcObject = null;
    pendingRemoteIceRef.current = [];
  };

  const endCall = () => {
    cleanup(true);
    setPhase("ended");
    setTimeout(onClose, 400);
  };

  const buildPc = (): RTCPeerConnection => {
    const pc = new RTCPeerConnection({ iceServers: iceServersRef.current });
    pc.onicecandidate = e => { if (e.candidate) sendSig({ type: "ice", candidate: e.candidate }); };
    pc.ontrack = e => {
      if (audioElRef.current && e.streams[0]) {
        audioElRef.current.srcObject = e.streams[0];
        audioElRef.current.play().catch(() => { /* ignore */ });
      }
    };
    pc.onconnectionstatechange = () => {
      const s = pc.connectionState;
      if (s === "connected") setPhase("in_call");
      else if (s === "failed" || s === "closed" || s === "disconnected") {
        if (phaseRef.current !== "ended") { setError("接続が切断されました"); endCall(); }
      }
    };
    return pc;
  };

  // ── 発信フロー（offer側） ────────────────────────────────
  const startOffer = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      localStreamRef.current = stream;
      const pc = buildPc();
      pcRef.current = pc;
      stream.getTracks().forEach(t => pc.addTrack(t, stream));
      const offer = await pc.createOffer({ offerToReceiveAudio: true });
      await pc.setLocalDescription(offer);
      // invite に SDPオファーと発信者名を同梱して送る（端末が応答したら answer が返る）
      sendSig({ type: "invite", sdp: offer, caller: adminUserName() });
      setPhase("ringing");
    } catch {
      setError("マイクを利用できませんでした。ブラウザのマイク権限を許可してください。");
      setPhase("ended");
    }
  };

  const toggleMute = () => {
    const s = localStreamRef.current;
    if (!s) return;
    const next = !muted;
    s.getAudioTracks().forEach(t => { t.enabled = !next; });
    setMuted(next);
  };

  useEffect(() => {
    const companyId = adminCompanyId();
    const token = getToken() ?? "";
    const wsBase = getApiBase().replace(/^http/, "ws"); // https://.../api -> wss://.../api
    const url = `${wsBase}/voice/signal?role=admin&company_id=${encodeURIComponent(companyId)}&device_id=${encodeURIComponent(deviceId)}&token=${encodeURIComponent(token)}`;

    let ws: WebSocket;
    try { ws = new WebSocket(url); } catch { setError("接続に失敗しました"); setPhase("ended"); return; }
    wsRef.current = ws;
    setPhase("calling");

    ws.onmessage = async ev => {
      let msg: { type: string; [k: string]: unknown };
      try { msg = JSON.parse(typeof ev.data === "string" ? ev.data : ""); } catch { return; }
      switch (msg.type) {
        case "ice_config":
          if (Array.isArray(msg["iceServers"])) iceServersRef.current = msg["iceServers"] as IceServer[];
          // ICE設定(TURN込み)を受け取ってから発信する
          if (phaseRef.current === "calling") await startOffer();
          break;
        case "peer_status":
          setPeerOnline(Boolean(msg["online"]));
          if (msg["online"] === false && (phaseRef.current === "ringing" || phaseRef.current === "calling")) {
            setError("端末がオフラインです（アプリが起動していません）");
          }
          break;
        case "accept":
          setPhase("connecting");
          break;
        case "answer": {
          const pc = pcRef.current;
          if (pc && msg["sdp"]) {
            await pc.setRemoteDescription(new RTCSessionDescription(msg["sdp"] as RTCSessionDescriptionInit));
            for (const c of pendingRemoteIceRef.current) { try { await pc.addIceCandidate(c); } catch { /* ignore */ } }
            pendingRemoteIceRef.current = [];
          }
          break;
        }
        case "ice": {
          const cand = msg["candidate"] as RTCIceCandidateInit;
          const pc = pcRef.current;
          if (pc && pc.remoteDescription) { try { await pc.addIceCandidate(cand); } catch { /* ignore */ } }
          else pendingRemoteIceRef.current.push(cand);
          break;
        }
        case "reject":
          setError(msg["reason"] === "busy" ? "端末が通話中です" : "着信が拒否されました");
          endCall();
          break;
        case "bye":
          if (phaseRef.current !== "ended") { setError("通話が終了しました"); endCall(); }
          break;
      }
    };
    ws.onerror = () => { setError("接続エラーが発生しました"); };
    ws.onclose = () => { if (phaseRef.current !== "ended" && phaseRef.current !== "in_call") { /* 呼出前後の切断 */ } };

    return () => {
      cleanup(phaseRef.current === "in_call" || phaseRef.current === "ringing" || phaseRef.current === "connecting");
      try { ws.close(); } catch { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  const label =
    phase === "calling" ? "呼び出し準備中…" :
    phase === "ringing" ? "呼び出し中…（相手の応答待ち）" :
    phase === "connecting" ? "接続中…" :
    phase === "in_call" ? "通話中" :
    phase === "ended" ? "終了" : "";

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={endCall}>
      <div onClick={e => e.stopPropagation()}
        style={{ width: 360, maxWidth: "92vw", background: "#fff", borderRadius: 16, padding: 28, textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <audio ref={audioElRef} autoPlay />
        <div style={{ fontSize: 44, marginBottom: 8 }}>{phase === "in_call" ? "🎧" : "📞"}</div>
        <div style={{ fontWeight: 700, fontSize: 18, color: "#0f172a" }}>{deviceName || "端末"}</div>
        <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
          {peerOnline === false ? "○ 端末オフライン" : peerOnline ? "● 端末オンライン" : "接続確認中…"}
        </div>
        <div style={{ fontSize: 15, color: "#0ea5e9", fontWeight: 600, margin: "16px 0" }}>{label}</div>

        {error && <div style={{ fontSize: 13, color: "#dc2626", marginBottom: 12 }}>{error}</div>}

        <div style={{ display: "flex", gap: 16, justifyContent: "center", marginTop: 8 }}>
          {phase === "in_call" && (
            <button onClick={toggleMute}
              style={{ width: 60, height: 60, borderRadius: "50%", border: "none", cursor: "pointer", fontSize: 22, background: muted ? "#94a3b8" : "#e2e8f0" }}>
              {muted ? "🔇" : "🎙"}
            </button>
          )}
          <button onClick={endCall}
            style={{ width: 60, height: 60, borderRadius: "50%", border: "none", cursor: "pointer", fontSize: 22, background: "#ef4444", color: "#fff" }}>
            {phase === "ended" ? "✕" : "📵"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CallModal;
